/**
 * PATH: packages/job-hub/src/jobs/access/resolveEffectiveJobCapabilities.ts
 * FILE_ID: JOBHUB-JOBS-ACCESS-EFFECTIVE-001
 * PURPOSE: Pure JH-16A intersection of host capabilities × job access meta.
 * WHY: Team/Access and header write controls must share one decision function
 *      so list/detail cannot diverge on Guest vs Key Job Person affordances.
 */
import type { JobHubCapabilities } from '../jobHubDataAdapter'
import type {
  EffectiveJobCapabilities,
  JobAccessMeta,
  OrganisationJobIntent,
} from './types'

export type AccessQueryStatus = 'pending' | 'success' | 'error' | 'idle'

export function isJobTeamAccessHost(capabilities?: JobHubCapabilities | null): boolean {
  return capabilities?.jobTeamAccess === true
}

/**
 * Resolve effective job-detail write/manage affordances.
 *
 * - Non-Django hosts (teamAccessSurface false): preserve historical
 *   "all org members can edit" behaviour — no access poll required.
 * - Django team-access hosts: fail closed on pending/error; require
 *   canEdit / canManageJobTeam from the existing access meta API.
 */
export function resolveEffectiveJobCapabilities(args: {
  host?: JobHubCapabilities | null
  access?: JobAccessMeta | null
  accessStatus: AccessQueryStatus
  organisationIntent?: OrganisationJobIntent | null
  intentStatus?: AccessQueryStatus
}): EffectiveJobCapabilities {
  const teamAccessSurface = isJobTeamAccessHost(args.host)
  const intentUnavailable = args.intentStatus === 'pending'
    || args.intentStatus === 'idle'
    || args.intentStatus === 'error'
  const intent = args.organisationIntent ?? {}
  const hostAllowsWrites = args.host?.jobWrites !== false

  if (!teamAccessSurface) {
    const canWriteJob = hostAllowsWrites
      && !intentUnavailable
      && intent.jobWrites !== false
    return {
      teamAccessSurface: false,
      accessPending: false,
      accessUnavailable: false,
      intentUnavailable,
      accessLevel: null,
      canWriteJob,
      canManageJobTeam: false,
      canUndo: canWriteJob && intent.undo !== false,
      keymapEnabled: !intentUnavailable && intent.keymap !== false,
      readOnly: !canWriteJob,
    }
  }

  const pending = args.accessStatus === 'pending' || args.accessStatus === 'idle'
  if (pending) {
    return {
      teamAccessSurface: true,
      accessPending: true,
      accessUnavailable: false,
      intentUnavailable,
      accessLevel: null,
      canWriteJob: false,
      canManageJobTeam: false,
      canUndo: false,
      keymapEnabled: false,
      readOnly: true,
    }
  }

  if (args.accessStatus === 'error' || !args.access) {
    return {
      teamAccessSurface: true,
      accessPending: false,
      accessUnavailable: true,
      intentUnavailable,
      accessLevel: null,
      canWriteJob: false,
      canManageJobTeam: false,
      canUndo: false,
      keymapEnabled: false,
      readOnly: true,
    }
  }

  const canWriteJob = !intentUnavailable
    && hostAllowsWrites
    && intent.jobWrites !== false
    && args.access.canEdit === true
  const canManageJobTeam = !intentUnavailable
    && intent.jobTeamAccess !== false
    && args.access.canManageJobTeam === true
  return {
    teamAccessSurface: true,
    accessPending: false,
    accessUnavailable: false,
    intentUnavailable,
    accessLevel: args.access.accessLevel ?? null,
    canWriteJob,
    canManageJobTeam,
    canUndo: canWriteJob && intent.undo !== false,
    keymapEnabled: !intentUnavailable && intent.keymap !== false,
    readOnly: !canWriteJob,
  }
}
